import React, { useEffect, useRef, useState } from 'react';
import { Aside } from '../shared/components/Aside/Aside';
import { useParticipants } from '../shared/contexts/ParticipantsProvider';
import { useUIState } from '../shared/contexts/UIStateProvider';
import { useMessageSound } from '../shared/hooks/useMessageSound';
import { useChat } from '../shared/contexts/ChatProvider';
import { theme } from '../../../../common/styles/theme';
import { alpha, Box, IconButton, Stack, TextField } from '@mui/material';
import { TelegramIcon } from '../../../../../icons/TelegramIcon';
import { CloseIcon } from '../../../../../icons/CloseIcon';

export const CHAT_ASIDE = 'chat';

export const ChatAside = () => {
	const { showAside, setShowAside } = useUIState();
	const { sendMessage, chatHistory, hasNewMessages, setHasNewMessages } = useChat();
	const { localParticipant } = useParticipants() as any;
	const [newMessage, setNewMessage] = useState('');
	const playMessageSound = useMessageSound();

	const chatWindowRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Clear out any new message notifications if we're showing the chat screen
		if (showAside === CHAT_ASIDE) {
			setHasNewMessages(false);
		}
	}, [showAside, chatHistory.length, setHasNewMessages]);

	useEffect(() => {
		if (hasNewMessages && showAside !== CHAT_ASIDE) {
			playMessageSound();
		}
	}, [playMessageSound, showAside, hasNewMessages]);

	useEffect(() => {
		if (chatWindowRef.current) {
			chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
		}
	}, [chatHistory?.length]);

	const isLocalUser = (id: any) => (
		id === localParticipant.user_id || // todo: no such field?
		id === localParticipant.sessionId
	);

	const sendMessageHandler = (e?: any) => {
		e?.preventDefault();
		if (!newMessage) return;
		sendMessage(newMessage);
		setNewMessage('');
	};

	if (!showAside || showAside !== CHAT_ASIDE) {
		return null;
	}

	return (
		<Aside
			className={'aside-chat'}
			onClose={() => setShowAside(false)}
			sx={{

				'.chat-footer': {
					flexFlow: 'row nowrap',
					p: theme.spacing(),
					display: 'flex',
					position: 'relative',
					borderTop: `1px solid ${theme.palette.grey[400]}`,
				},

				'@media (max-width: 600px)': {
					maxWidth: '60vw',
				},

			}}
		>
			<Stack
				className="messages-container"
				ref={chatWindowRef as any}
				gap={1}
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
				}}
			>
				<Stack
					flexDirection={'row'}
					justifyContent={'space-between'}
					alignItems={'center'}
				>
					<Box
						sx={{
							p: theme.spacing(),
							fontWeight: 600,
						}}
					>
						Chat
					</Box>

					<IconButton
						onClick={() => setShowAside(false)}
						sx={{
							color: theme.palette.grey[400],
							borderRadius: '50%',

							'&:hover, &:active, &:focus': {
								color: theme.palette.grey[900],
							},
							'.MuiSvgIcon-root': { width: theme.spacing(3), height: theme.spacing(3) }
						}}
					>
						<CloseIcon />
					</IconButton>
				</Stack>

				{chatHistory.map((chatItem: any) => {
					return (
						<Stack
							className={isLocalUser(chatItem.senderID) ? 'local' : ''}
							key={chatItem.id}
							sx={{
								m: theme.spacing(0, 1),
								fontSize: theme.typography.subtitle2,
								wordBreak: 'break-word',

								'&.local': {
									alignItems: 'flex-end',

									'& .content': {
										borderRadius: theme.spacing(.5, 0, .5, .5),
										background: alpha(theme.palette.primary.main, .1),
									},
								},
							}}
						>
							<Box
								className={'sender'}
								sx={{
									color: theme.palette.grey[500],
									fontWeight: 500,
									fontSize: theme.typography.subtitle1,
								}}
							>
								{chatItem.sender}
							</Box>
							<Box
								className={'content'}
								sx={{
									p: theme.spacing(1),
									background: theme.palette.grey[50],
									borderRadius: theme.spacing(0, .5, .5, .5),
								}}
							>
								{chatItem.message}
							</Box>
						</Stack>
					);
			})}
			</Stack>
			<form onSubmit={sendMessageHandler}>
				<Stack
					flexDirection={'row'}
					sx={{
						position: 'relative',
						maxHeight: '50vh',
						p: theme.spacing(),
					}}
				>
					<TextField
						value={newMessage}
						placeholder={'Type message here'}
						multiline
						onChange={(e) => setNewMessage(e.target.value)}
						onKeyDown={(e: React.KeyboardEvent) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								sendMessageHandler();
							}
						}}
						variant={'filled'}
						sx={{
							width: 1,
							maxHeight: 1,
							minHeight: theme.spacing(7),

							'.MuiInputBase-root': {
								height: 1,
								pr: theme.spacing(10),
								overflowY: 'auto',
							},
						}}
					/>
					<IconButton
						disabled={!newMessage}
						type={'submit'}
						sx={{
							position: 'absolute',
							right: theme.spacing(2),
							bottom: theme.spacing(2.25),
							color: theme.palette.primary.main,
							background: theme.palette.common.white,
							borderRadius: '50%',
						}}
					>
						<TelegramIcon />
					</IconButton>
				</Stack>
			</form>
		</Aside>
	);
};
